import { cn } from '@/lib/utils';
import { Icons } from '@/libs/components/src/lib/icons';
import { Button, ButtonColors } from '@chiroup/components/Button';
import { Textarea } from '@chiroup/components/form/Textarea';
import { Evaluation } from '@chiroup/core/types/Evaluation.type';
import { Macro } from '@chiroup/core/types/Macro';
import { RadioGroup } from '@headlessui/react';
import {
  MinusCircle as MinusCircleIcon,
  Minus as MinusIcon,
  PlusCircle as PlusCircleIcon,
} from 'lucide-react';

import { Icon } from '@chiroup/components/Icon';
import { Icons as SharedIcons } from '@chiroup/components/icons';
import React, { use, useState } from 'react';
import { Link } from 'react-router-dom';
import { MeContext } from '../../../../../contexts/me.context';

type Props = {
  testId: number;
  onChange: (prop: 'side' | 'value' | 'testNotes', value: string | null) => void;
  value?: {
    side: 'left' | 'right';
    value: 'positive' | 'negative' | null;
    testNotes?: string;
    typicallyPositive?: boolean;
    typicallyNegative?: boolean;
  };
  test: Evaluation;
  removeTest: (testId: number) => void;
  disabled?: boolean;
  /** Called instead of expanding the test-notes details (trial SOAP sandbox
   *  shows an informational pop-up for the "+" details control). */
  onDetailsIntercept?: () => void;
};

const OrthoTest: React.FC<Props> = ({
  testId,
  test,
  onChange,
  value,
  removeTest,
  disabled,
  onDetailsIntercept,
}) => {
  const [showNote, setShowNote] = useState(!!value?.testNotes);
  const { me } = use(MeContext);

  return (
    <div>
      {test && (
        <form>
          <div className="overflow-visible">
            <ul className="flex flex-col gap-2 p-4">
              <div className="flex flex-row items-center justify-between">
                <p className="text-sm font-medium text-gray-900 truncate dark:text-dark-gray-50 relative">
                  {test?.name}
                  <span className="ml-2">
                    <Link to={`${testId}`}>
                      <Button icon={Icons.link} color={ButtonColors.plainIcon} />
                    </Link>
                  </span>
                </p>
                {!disabled && (
                  <Icon
                    icon={SharedIcons.close}
                    className="w-4 h-4 text-gray-300 cursor-pointer hover:text-gray-400"
                    onClick={() => removeTest(testId)}
                  />
                )}
              </div>
              <div className="sm:flex sm:justify-between sm:items-center">
                <div className="flex flex-row gap-4">
                  <RadioGroup
                    value={value?.value ?? null}
                    onChange={(value: 'positive' | 'negative' | null) => {
                      if (disabled) return;
                      onChange('value', value);
                    }}
                    disabled={disabled}
                    className="flex flex-row gap-1"
                  >
                    <div
                      className={[
                        'border-4 rounded-full -ml-1 cursor-pointer',
                        value?.typicallyPositive
                          ? 'border-primary-300 dark:border-primary-600'
                          : 'border-white dark:border-dark-gray-900',
                      ].join(' ')}
                    >
                      <RadioGroup.Option
                        value="positive"
                        disabled={disabled}
                        onClick={() => {
                          if (disabled) return;
                          if (value?.value === 'positive') {
                            onChange('value', null);
                          }
                        }}
                      >
                        <RadioGroup.Label as="span" className="sr-only">
                          Positive
                        </RadioGroup.Label>
                        <span
                          aria-hidden="true"
                          className={cn(
                            value?.value === 'positive'
                              ? `bg-primary-500 text-white`
                              : 'bg-gray-300 dark:bg-dark-gray-600 text-gray-400',
                            'h-10 w-10 border border-gray-600/10 rounded-full flex items-center justify-center ring-2 ring-white dark:ring-dark-gray-500'
                          )}
                        >
                          <div>
                            <Icon icon={SharedIcons.add} className="w-4" />
                          </div>
                        </span>
                      </RadioGroup.Option>
                    </div>
                    <div
                      className={[
                        'border-4 rounded-full cursor-pointer',
                        value?.typicallyNegative
                          ? 'border-red-300 dark:border-red-600'
                          : 'border-white dark:border-dark-gray-900',
                      ].join(' ')}
                    >
                      <RadioGroup.Option
                        value="negative"
                        disabled={disabled}
                        onClick={() => {
                          if (disabled) return;
                          if (value?.value === 'negative') {
                            onChange('value', null);
                          }
                        }}
                      >
                        <RadioGroup.Label as="span" className="sr-only">
                          Negative
                        </RadioGroup.Label>
                        <span
                          aria-hidden="true"
                          className={cn(
                            value?.value === 'negative'
                              ? `bg-red-600 text-white`
                              : 'bg-gray-300 dark:bg-dark-gray-600 text-gray-400',
                            'h-10 w-10 border border-gray-600/10 rounded-full flex items-center justify-center ring-2 ring-white dark:ring-dark-gray-500'
                          )}
                        >
                          <div>
                            <MinusIcon className="w-4" />
                          </div>
                        </span>
                      </RadioGroup.Option>
                    </div>
                  </RadioGroup>
                </div>
                <div>
                  <button
                    type="button"
                    disabled={disabled}
                    className={[
                      'relative inline-flex items-center rounded-l-md border px-8 sm:px-4 py-1 text-sm font-medium transition duration-150 ease-in-out',
                      value?.side === 'left'
                        ? 'bg-red-600 border-red-600 text-white dark:text-dark-gray-900 outline-none border-r-transparent'
                        : 'border-gray-300 dark:border-dark-gray-700 bg-white dark:bg-dark-gray-900 text-gray-700 dark:text-dark-gray-700 border-r-transparent',
                      disabled ? 'opacity-50 cursor-not-allowed' : '',
                    ].join(' ')}
                    onClick={() => {
                      if (disabled) return;
                      onChange('side', 'left');
                    }}
                  >
                    L
                  </button>
                  <button
                    type="button"
                    disabled={disabled}
                    className={[
                      'relative inline-flex items-center rounded-r-md border px-8 sm:px-4 py-1 text-sm font-medium transition duration-150 ease-in-out',
                      value?.side === 'right'
                        ? 'bg-red-600 border-red-600 text-white dark:text-dark-gray-900 outline-none'
                        : 'border-gray-300 dark:border-dark-gray-700 bg-white dark:bg-dark-gray-900 text-gray-700 dark:text-dark-gray-700',
                      disabled ? 'opacity-50 cursor-not-allowed' : '',
                    ].join(' ')}
                    onClick={() => {
                      if (disabled) return;
                      onChange('side', 'right');
                    }}
                  >
                    R
                  </button>
                </div>
              </div>
              <div className="flex flex-row justify-around">
                {showNote ? (
                  <MinusCircleIcon
                    className="w-6 h-6 text-gray-300 cursor-pointer hover:text-gray-400"
                    onClick={() => {
                      if (onDetailsIntercept) {
                        onDetailsIntercept();

                        return;
                      }
                      setShowNote(prev => !prev);
                    }}
                  />
                ) : (
                  <PlusCircleIcon
                    className="w-6 h-6 text-gray-300 cursor-pointer hover:text-gray-400"
                    onClick={() => {
                      if (onDetailsIntercept) {
                        onDetailsIntercept();

                        return;
                      }
                      setShowNote(prev => !prev);
                    }}
                  />
                )}
              </div>
              {showNote && (
                <div className="flex py-2">
                  <Textarea
                    className="w-full"
                    rows={3}
                    aria-label="Test notes"
                    value={value?.testNotes}
                    onChange={val => onChange('testNotes', val)}
                    macros={me.macros as Macro[]}
                    disabled={disabled}
                  />
                </div>
              )}
            </ul>
          </div>
        </form>
      )}
    </div>
  );
};

export default OrthoTest;
